/**
 * Shared plumbing for <cart-upsell-toggle> (blocks/cart-upsell.liquid) and <cart-upsell-cta>
 * (blocks/cart-upsell-cta.liquid) — both add/remove a fee product as its own, separate cart line item via the
 * storefront Cart AJAX API, then re-render affected sections through the theme's existing Section Rendering API
 * bundling contract (cart:prepare-bundled-sections / cart:change), exactly the way theme.js's ProductForm and
 * LineItemQuantity components already do it. Only the theme's documented public cart events are used: variant:add,
 * cart:change, cart:error, cart:refresh.
 *
 * <cart-upsell-toggle> is a checkbox: checking/unchecking it explicitly adds/removes the fee.
 * <cart-upsell-cta> is a themed checkout-style button (no checkbox): the fee is only ever added inside its own
 * click handler, never automatically. CartUpsellElement's shared #_reconcile() below deliberately never adds a
 * line for either element type — see _ensureFeeLine's and CartUpsellCta's own comments for why that split matters.
 *
 * On the full /cart page, a successful checkbox mutation patches the page's own section in place via
 * #_refreshCartPageContent() (fetch ?section_id=<derived id>, swap in the HTML) rather than reloading — nothing on the
 * cart page listens for cart:prepare-bundled-sections/cart:change to patch its own DOM the way <cart-drawer>
 * does, so without this the page's table/total would go stale after a mutation triggered from the drawer while
 * viewing /cart. An earlier version used window.location.reload() here instead (matching LineItemQuantity's own
 * existing pageType === "cart" behavior in theme.js) - QA found that a reload cancels every OTHER request still
 * in flight on the page, including ones this code has nothing to do with (e.g. a cross-sell "add to cart"
 * fired moments earlier), silently discarding them even after they'd already reached Shopify's server. See
 * #_refreshCartPageContent's own comment. <cart-upsell-cta> doesn't need this: after adding the fee it
 * navigates to checkout itself via window.location (see CartUpsellCta#_onClick) - a real navigation away is
 * that button's whole purpose, not an incidental side effect of an otherwise-small toggle.
 *
 * CartUpsellToggle's fee line is meant to persist like a checked checkbox: #_reconcile (defined once on
 * CartUpsellElement, used as-is by CartUpsellToggle) only auto-removes it once the cart stops being eligible (the
 * qualifying product was removed, or the cart is down to just fee lines) — a customer should never keep paying
 * for a fee with nothing left in the cart for it to apply to — and otherwise self-heals the line via
 * #_ensureFeeLine against whatever Liquid just rendered as correct for this instance: the target variant (for
 * pricing_mode "percentage_of_subtotal", e.g. "Shipping Protect", once the subtotal moves) and the line's
 * quantity, which #_ensureFeeLine always forces back to exactly 1. That quantity correction is what closes a
 * race QA found where two tabs (or a fast double-click) clicking/checking around the same moment could each
 * see "not added yet" and both add, stacking the same fee to quantity 2+ — see #_ensureFeeLine's own comment.
 * #_reconcile also compares what this instance rendered against the live cart independently of whether a write
 * is needed, and refreshes the UI on any mismatch — closing a second QA-found gap where a customer could outrun
 * their own request (e.g. uncheck in the drawer, then land on /cart before that removal's own render catches
 * up), leaving a checkbox showing the wrong state until a manual reload even though the cart itself was already
 * correct. CartUpsellCta's fee is meant to be ephemeral instead — see its own #_reconcile override below for why.
 *
 * Every cart mutation triggers a full drawer section re-render, which replaces these custom elements with a
 * brand-new instance — a fresh, enabled control that knows nothing about an operation still in flight on the
 * element it replaced. withLock() closes that gap by forcing every check/uncheck/reconcile/click across this
 * whole file to run strictly one at a time, however many element generations, block instances, fee products,
 * or element types it spans.
 *
 * That lock is GLOBAL (one shared queue for every product this file manages), not per-product. An earlier
 * version keyed it per fee product id instead, on the reasoning that unrelated fees shouldn't block each
 * other. QA proved that reasoning wrong with live network captures in both directions: unchecking one fee and
 * immediately clicking a DIFFERENT fee's checkout button, and checking one fee and immediately clicking a
 * different fee's checkout button, both landed two genuinely concurrent /cart/add.js|change.js calls on
 * Shopify's own Cart API for the same cart session - and Shopify does not appear to isolate those safely.
 * Captured evidence: two /cart.js reads fired 1ms apart returned CONTRADICTORY cart states (one correctly
 * showing a fee removed, the other showing it still present) - a genuine server-side race, not anything this
 * file's own logic could reason its way around. Serializing every mutation this file makes, regardless of
 * which product it targets, is what actually closes it: two writes from this file can no longer be in flight
 * against the cart at the same instant, so there is nothing left for Shopify's backend to race.
 *
 * The obvious cost: two unrelated fee operations that used to run in parallel now queue behind each other,
 * adding real latency to that specific sequence. Accepted deliberately - a slower toggle is a far smaller
 * problem than a charge the customer explicitly agreed to silently reversing itself before checkout.
 */
let cartUpsellLock = Promise.resolve();

function withLock(fn) {
  const run = cartUpsellLock.then(fn, fn);
  cartUpsellLock = run.catch(() => {});
  return run;
}

class CartUpsellElement extends HTMLElement {
  _abortController;
  #priceGuardObservers = [];
  #priceGuardRestoring = false;

  connectedCallback() {
    this._abortController = new AbortController();
    this._installPriceGuard();

    // Captured once, here, while this element is guaranteed to be attached - not re-derived from
    // this.closest('.shopify-section') at click/reconcile time. A cart:change dispatched by something this
    // file has no connection to (e.g. theme.js's own LineItemQuantity changing an existing line's quantity)
    // makes <cart-drawer> replace its ENTIRE contents on every occurrence, detaching this exact element from
    // the document. #_fetchLiveVariantId and #_refreshCartPageContent used to re-query this.closest(...) at
    // call time, which returns null once that happens - silently falling back to this.dataset.variantId, a
    // snapshot frozen from before whatever just changed. Confirmed live: change a line's quantity, then
    // immediately click "Checkout with Cover" before the drawer's own redraw settles, and the fee gets priced
    // against the PRE-change subtotal despite #_fetchLiveVariantId doing a genuinely fresh server fetch - the
    // fetch was fresh, but the fallback path feeding it a section id wasn't. A section's id never changes for
    // the lifetime of a page (a given block always lives in the same section), so caching it here removes the
    // dependency on this element staying attached entirely - only this.dataset.blockId (a static attribute,
    // never re-queried from the DOM) is still needed at call time.
    this._sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');

    document.documentElement.addEventListener(
      'cart:change',
      () => withLock(this._reconcile),
      { signal: this._abortController.signal }
    );
    withLock(this._reconcile);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this.#priceGuardObservers.forEach((observer) => observer.disconnect());
  }

  /**
   * Easify's product-options script has been observed rewriting price-looking text elsewhere in the DOM
   * (confirmed via a MutationObserver trace: it targets elements generically, not just its own UI, and ignores
   * the theme's existing tpo_ignore escape hatch for this code path). Rather than guess at a selector that
   * avoids it — which could break on Easify's next update — this snapshots the correct markup for every
   * [data-price-guard] element once and reverts any external change to it. Scoped to [data-price-guard]
   * elements only, not the checkbox/button itself, so it can never fight this component's own checked/disabled
   * state changes.
   *
   * Guards EVERY matching element, not just the first: CartUpsellCta's loading-spinner markup
   * (blocks/cart-upsell-cta.liquid's cart-upsell-cta__loader) hit this same Easify behavior live — confirmed by
   * inspecting the DOM mid-click, its icon SVG had been silently replaced with Easify's own (fee-less) price
   * text — even though it's a second, separate guarded element from the button's own price span. A single
   * querySelector/observer pair only ever protected the first match.
   */
  _installPriceGuard() {
    const guardedElements = this.querySelectorAll('[data-price-guard]');

    guardedElements.forEach((guarded) => {
      const canonicalHTML = guarded.innerHTML;

      const observer = new MutationObserver(() => {
        if (this.#priceGuardRestoring || guarded.innerHTML === canonicalHTML) return;
        this.#priceGuardRestoring = true;
        guarded.innerHTML = canonicalHTML;
        this.#priceGuardRestoring = false;
      });

      observer.observe(guarded, { childList: true, subtree: true, characterData: true, attributes: true });
      this.#priceGuardObservers.push(observer);
    });
  }

  /**
   * Checkout renders product/variant/line-item data directly — it never reads section or block settings — so
   * this instance's description is only visible there if it's sent along as a cart line item property when the
   * fee line is created.
   */
  _lineItemProperties() {
    return this.dataset.description ? { Details: this.dataset.description } : {};
  }

  /**
   * Removes this instance's own line from the cart — used both by CartUpsellToggle's own change handler and by
   * #_reconcile()'s eligibility auto-removal.
   */
  async _removeLine(lineKey) {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0, sections: sectionsToBundle.join(',') }),
    });

    const cartContent = await response.json();

    if (!response.ok) {
      throw new Error(cartContent.description || cartContent.message);
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cartContent } })
    );

    if (window.themeVariables.settings.pageType === 'cart') {
      await this._refreshCartPageContent();
    }
  }

  /**
   * Patches the /cart page's own section in place instead of reloading the page - the same technique
   * theme.js's own CartDrawer#refreshCart_fn already uses for the drawer (fetch `?section_id=<id>`, swap in
   * the returned HTML), just applied to the page body instead of the drawer overlay.
   *
   * The section id comes from this._sectionId (cached once in #connectedCallback - see that field's own
   * comment for why it's no longer re-derived from the live DOM here), exactly like theme.js's own
   * extractSectionId() - Online Store 2.0 assigns JSON-template sections a dynamic id like
   * "template--12345__main", not the plain "main" key from templates/cart.json, and that number varies per
   * theme/environment. Confirmed live: fetching with the hardcoded literal "main" 404s; the derived id
   * round-trips correctly.
   *
   * This replaces an earlier version that called window.location.reload() here, matching an existing theme
   * convention (LineItemQuantity and AbstractCartDiscount's own toggleDiscount both reload on pageType ===
   * "cart" too). QA found a real cost to that: a reload tears down the ENTIRE page's JS context, which
   * cancels every other request still in flight on the page - including one the customer fired moments
   * earlier from a completely different control (e.g. adding a cross-sell product just before checking this
   * box). That request had already reached Shopify's server, but the reload cut the client off before it
   * could process the response, so the page came back showing the cart from before the add ever happened -
   * with only this instance's own change surviving. Swapping in fresh section HTML never unloads the page,
   * so it structurally cannot cancel anything else running on it.
   */
  async _refreshCartPageContent() {
    if (!this._sectionId) return;

    const domId = `shopify-section-${this._sectionId}`;
    const section = document.getElementById(domId);
    if (!section) return;

    const html = await (await fetch(`${window.location.pathname}?section_id=${this._sectionId}`)).text();
    const freshSection = new DOMParser().parseFromString(html, 'text/html').getElementById(domId);

    if (freshSection) {
      section.innerHTML = freshSection.innerHTML;
    }
  }

  /**
   * Re-fetches this instance's own block fresh from the server — Shopify.routes.root + "?section_id=<id>",
   * the exact same cross-page-safe technique theme.js's own CartDrawer#refreshCart_fn already uses to refresh
   * the drawer from any page — and returns the variant id THAT render computed against the live cart, instead
   * of trusting this.dataset.variantId, which is only a snapshot from whenever this element last rendered.
   * Deliberately uses Shopify.routes.root, not window.location.pathname/#_refreshCartPageContent's technique:
   * this instance can live inside the cart drawer, which renders on every page, not just /cart, and section
   * rendering only cares about the section_id param — confirmed against theme.js's own CartDrawer, which
   * refreshes the same way regardless of what page triggered it.
   *
   * QA found a real gap this closes for percentage_of_subtotal fees (e.g. "Shipping Protect"): adding a
   * second product and immediately clicking "Checkout with Cover", before this button's own block had a
   * chance to re-render for the new, higher subtotal, added the fee at the OLD, now-too-low subtotal's
   * variant/price tier — #_ensureFeeLine was faithfully correcting the line to match a target that was
   * already stale the moment the click fired.
   *
   * The section id comes from this._sectionId (cached once in #connectedCallback), not from
   * this.closest('.shopify-section') at call time - a live DOM query used to be able to return null here even
   * though the fetch itself would have been perfectly fresh, because a cart:change fired by something else
   * entirely (e.g. changing an existing line's quantity via theme.js's own LineItemQuantity) makes
   * <cart-drawer> replace its whole contents, detaching this exact element - see this._sectionId's own
   * comment. Falls back to this.dataset.variantId only if this._sectionId itself was never available (e.g.
   * this element was never actually inside a .shopify-section to begin with) or the block can't be found in
   * the fresh response, so a genuine lookup failure never blocks the operation outright.
   */
  async _fetchLiveVariantId() {
    const blockId = this.dataset.blockId;
    if (!this._sectionId || !blockId) return Number(this.dataset.variantId);

    const html = await (await fetch(`${Shopify.routes.root}?section_id=${this._sectionId}`)).text();
    const freshBlock = Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-block-id]')).find(
      (element) => element.dataset.blockId === blockId
    );

    return freshBlock ? Number(freshBlock.dataset.variantId) : Number(this.dataset.variantId);
  }

  /**
   * Ensures exactly one line for this instance's fee product exists in the cart, at the variant id a fresh
   * server render computes for it right now (see #_fetchLiveVariantId) and at quantity exactly 1 — regardless
   * of what was there before. Resolves to { added: false } only when the live cart already matched exactly,
   * so nothing needed to be written; otherwise { added: true, responseJson } after correcting it. Throws on
   * failure.
   *
   * Four corrections close gaps QA found by driving the real Cart AJAX API directly:
   *   1. The target variant itself is re-derived fresh (#_fetchLiveVariantId) rather than trusted from
   *      this.dataset.variantId, which can already be stale by the time this runs — see that method's
   *      comment for the "add a product, immediately click Checkout with Cover" race this closes.
   *   2. A stale line for the WRONG variant (e.g. a percentage_of_subtotal target from before the subtotal
   *      changed) is removed before adding the correct one, so a subtotal change never leaves two lines for
   *      the same fee product.
   *   3. The add always goes through /cart/add.js, which MERGES into an existing line of the same
   *      variant+properties by summing quantity — exactly what happens when two browser tabs, or a fast
   *      double-click, both add around the same moment.
   *   4. A trailing /cart/change.js explicitly SETS the resulting line's quantity to a literal 1 — an
   *      absolute set, not "add 1 more". Because of that, whichever racing call's correction happens to run
   *      last always leaves the line at exactly 1, however many adds landed first: closing the double-charge
   *      race without needing a distributed lock.
   *
   * Called both from an explicit customer action (CartUpsellToggle's checkbox #_onChange, CartUpsellCta's
   * button click) AND from #_reconcile() as a self-heal pass. That second caller does not weaken the "never
   * add a charge the customer didn't request" guarantee behind Shopify's App Store Requirements (1.1.9,
   * "can't automatically add or pre-select optional charges... only after... obtaining explicit buyer
   * consent") — #_reconcile only ever calls this when this.dataset.lineKey is already set, i.e. a fee line
   * the customer already added is present. It corrects an existing charge; it never originates one.
   */
  async _ensureFeeLine() {
    const productId = Number(this.dataset.productId);
    const properties = this._lineItemProperties();

    const [cart, targetVariantId] = await Promise.all([
      fetch(`${Shopify.routes.root}cart.js`).then((response) => response.json()),
      this._fetchLiveVariantId(),
    ]);
    // Matched by product_id, not variant_id - a fee product should only ever have one line in the cart at
    // a time regardless of pricing mode, and matching by variant_id in fixed_price mode misses the line
    // whenever the cart's actual variant differs from whatever selected_or_first_available_variant resolves
    // to at render time (see blocks/cart-upsell-cta.liquid's fee_line_item comment for the same issue there).
    const existingLine = cart.items.find((item) => item.product_id === productId);

    if (existingLine && existingLine.variant_id === targetVariantId && existingLine.quantity === 1) {
      return { added: false };
    }

    if (existingLine && existingLine.variant_id !== targetVariantId) {
      await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: existingLine.key, quantity: 0 }),
      });
    }

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: targetVariantId, quantity: 1, properties }] }),
    });

    const addJson = await addResponse.json();

    if (!addResponse.ok) {
      throw new Error(addJson.description || addJson.message);
    }

    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: addJson.items[0].key,
        quantity: 1,
        properties,
        sections: sectionsToBundle.join(','),
      }),
    });

    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || responseJson.message);
    }

    return { added: true, responseJson };
  }

  /**
   * Self-heals this instance's fee line against the live cart, without ever originating a charge the
   * customer didn't request — see #_ensureFeeLine's own comment for the guarantee this relies on.
   *
   * 1. Eligibility (all pricing modes): if this instance is no longer eligible (its qualifying product was
   *    removed, or the cart is down to just fee lines) but its fee is still added, remove it. A customer
   *    should never keep paying for a fee with nothing left in the cart for it to apply to.
   * 2. Otherwise, compares what THIS instance rendered (this.dataset.lineKey - was a fee line present at
   *    the moment Liquid ran) against a fresh cart fetch, in both directions:
   *      - A fee line exists live: hand it to #_ensureFeeLine to correct against whatever Liquid rendered as
   *        right for THIS instance - a stale percentage_of_subtotal target after the subtotal changed, or a
   *        quantity that's drifted away from 1. Liquid computes the correct target fresh on every server
   *        render (it always sees the live cart at render time), so this deliberately does NOT re-derive the
   *        target client-side - an earlier version did, using a DOM snapshot for which line items to exclude
   *        from the subtotal, and that snapshot went stale the instant the fee line itself changed, causing a
   *        visible overshoot-then-correct oscillation.
   *      - No fee line exists live: never adds one on its own - reconciling can only correct a charge that's
   *        already there, never originate one (see #_ensureFeeLine's comment for why that split matters).
   *    Either way, if what's live disagrees with what this.dataset.lineKey says was rendered, the display
   *    itself is stale even when nothing needed writing, so this refreshes the UI to match - see below.
   *
   *    The live re-check (rather than trusting this.dataset.lineKey on its own) matters for two related
   *    reasons found via QA:
   *      a) This exact method can run on a STALE, already-disconnected instance: unchecking
   *         CartUpsellToggle's own checkbox dispatches cart:change synchronously as part of removing the
   *         line, and that same dispatch is what THIS instance's own cart:change listener (registered in
   *         connectedCallback) reacts to - queuing this call before the section re-render has a chance to
   *         replace this element and abort its listener. this.dataset.lineKey on that stale instance is a
   *         snapshot from before the uncheck and still looks populated even though the line is already
   *         gone - trusting it alone used to make this call _ensureFeeLine, which found nothing live and
   *         "helpfully" added the fee straight back, turning an uncheck into a checkbox that flips itself
   *         back on.
   *      b) Separately, a customer can outrun their own request: uncheck in the drawer, then click "View
   *         Cart" before the removal's own network round trip finishes. The removal still lands server-side
   *         (that's why a manual refresh shows the right state), but the /cart page's own initial Liquid
   *         render can be generated a moment before it does, baking in a checkbox that shows checked even
   *         though the cart is already correct - and the same can happen in reverse when checking. Nothing
   *         about that needs a cart write, only a display refresh, which is what the final block below does.
   *
   * Runs once on connect — which happens after every relevant cart mutation, since the theme re-renders this
   * block's section on every cart:change — and again on cart:change directly, as a backstop for the rare case
   * this exact instance survives across multiple mutations without being replaced. Always called through
   * withLock().
   */
  _reconcile = async () => {
    const lineKey = this.dataset.lineKey;

    if (this.dataset.isEligible === 'false' && lineKey) {
      try {
        await this._removeLine(lineKey);
      } catch (error) {
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error?.message || error } }));
      }
      return;
    }

    try {
      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      const currentLine = cart.items.find((item) => item.product_id === Number(this.dataset.productId));
      const displayIsStale = Boolean(lineKey) !== Boolean(currentLine);

      let needsRefresh = displayIsStale;

      if (currentLine) {
        const { added } = await this._ensureFeeLine();
        needsRefresh = needsRefresh || added;
      }

      if (!needsRefresh) return;

      document.dispatchEvent(new CustomEvent('cart:refresh'));

      if (window.themeVariables.settings.pageType === 'cart') {
        await this._refreshCartPageContent();
      }
    } catch (error) {
      this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error?.message || error } }));
    }
  };
}

/**
 * <cart-upsell-toggle> (blocks/cart-upsell.liquid) — the checkbox display mode. Checking/unchecking explicitly
 * adds/removes the fee; see CartUpsellElement above for the shared reconcile/lock/add machinery this builds on.
 */
class CartUpsellToggle extends CartUpsellElement {
  #checkbox;

  connectedCallback() {
    super.connectedCallback();
    this.#checkbox = this.querySelector('input[type="checkbox"]');
    this.#checkbox?.addEventListener('change', this.#onChange, { signal: this._abortController.signal });
  }

  #onChange = async (event) => {
    const checkbox = event.currentTarget;
    const wasChecked = !checkbox.checked;
    const lineKey = this.dataset.lineKey;

    checkbox.disabled = true;

    await withLock(async () => {
      try {
        if (checkbox.checked) {
          const result = await this._ensureFeeLine();

          if (!result.added) {
            // _ensureFeeLine already confirmed the live cart matches exactly (right variant, quantity 1) with
            // no network mutation needed - just refresh the UI in case this checkbox's own rendered state
            // (e.g. after a stale page load) disagreed with that.
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            return;
          }

          const addedItems = Array.isArray(result.responseJson) ? result.responseJson : [result.responseJson];

          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          cartContent.sections = result.responseJson.sections;

          this.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart: cartContent } }));
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
          );

          if (window.themeVariables.settings.pageType === 'cart') {
            await this._refreshCartPageContent();
          }
        } else {
          if (!lineKey) {
            throw new Error('Missing cart line key for cart upsell line item.');
          }

          await this._removeLine(lineKey);
        }
      } catch (error) {
        checkbox.checked = wasChecked;
        this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error?.message || error } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    });

    checkbox.disabled = false;
  };
}

/**
 * <cart-upsell-cta> (blocks/cart-upsell-cta.liquid) — the button display mode. Renders a themed checkout-style
 * button whose price already previews what the total would become with the fee included (computed in Liquid
 * without touching the cart — see display_total in the block), but the fee itself is only ever added inside this
 * click handler, never automatically on render or reconcile. The theme's own, separate checkout button is left
 * completely untouched as the no-fee path. See CartUpsellElement#_ensureFeeLine's comment for the Shopify App
 * Store Requirements 1.1.9 rationale behind that split — the click here is the required explicit consent, and the
 * price already shown on the button beforehand is the required cost disclosure.
 *
 * Navigation to checkout is driven entirely by this handler (window.location, not a form submission) and the
 * button deliberately isn't named "checkout" — see the comment in blocks/cart-upsell-cta.liquid above this
 * element for why: Easify's product-options app hijacks any button[name="checkout"] click and redirects straight
 * to /checkout itself, bypassing this handler (and the fee-add fetch) entirely.
 *
 * Unlike CartUpsellToggle, this never carries a fee across page loads: this button always represents a clean,
 * not-yet-clicked state, so if a customer clicks it and then abandons checkout (e.g. hits back) instead of
 * completing it, the fee shouldn't linger as a surprise addition to the theme's own plain checkout button on
 * return. #_reconcile is overridden below to unconditionally remove this instance's own fee line whenever one is
 * already present at connect/cart:change time, regardless of eligibility — the base class's ineligibility-only
 * removal (inherited as-is by CartUpsellToggle, where an added fee is meant to persist like a checked checkbox
 * until the customer unchecks it or the cart stops being eligible) doesn't apply here. This can't race against
 * #_onClick's own add: that handler navigates away immediately after adding, with nothing in between that would
 * re-render this element or fire another cart:change on this page.
 */
class CartUpsellCta extends CartUpsellElement {
  #button;

  /**
   * Products with a click-triggered #_ensureFeeLine currently in flight - not tied to any one instance, since a
   * cart:change fired by something else entirely (e.g. unchecking a different fee) triggers a full drawer
   * redraw that replaces this exact element mid-click, same as the reasoning behind CartUpsellCta's own
   * ctaReconciledProducts tracking. Without this, aria-busy would be set on the OLD instance's DOM node, which
   * the redraw just discarded - the spinner would silently vanish for the remaining second or two the click's
   * network chain still has left, even though the click itself is still in flight. #connectedCallback below
   * re-applies aria-busy to any fresh instance for a product still marked busy here, so the spinner reads as
   * continuous across as many redraws as it takes.
   */
  static ctaBusyProducts = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#button = this.querySelector('button[type="submit"]');
    this.#button?.addEventListener('click', this.#onClick, { signal: this._abortController.signal });

    if (CartUpsellCta.ctaBusyProducts.has(this.dataset.productId)) {
      this.setAttribute('aria-busy', 'true');
      if (this.#button) this.#button.disabled = true;
    }
  }

  _reconcile = async () => {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    try {
      await this._removeLine(lineKey);
    } catch (error) {
      this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error?.message || error } }));
    }
  };

  /**
   * KNOWN UNRESOLVED GAP: changing an EXISTING line's quantity is handled entirely by theme.js's own
   * LineItemQuantity component, which fires its own independent /cart/change.js with no connection to this
   * file's withLock() at all. If a customer clicks this button while that write is still in flight,
   * #_fetchLiveVariantId's own fetch can be genuinely fresh (confirmed live via direct request-timeline
   * instrumentation: the section-render GET resolved ~740ms BEFORE the competing quantity write actually
   * committed on Shopify's server) and still price the fee off the pre-change subtotal. A retry-until-settled
   * wrapper around #_ensureFeeLine was tried and reverted - confirmed via the same live instrumentation to
   * still fail even in the drawer (no competing page reload there, unlike /cart), so it added latency without
   * closing the gap. Deliberately left unresolved rather than shipped with an unproven mitigation - narrow
   * window (needs the click within roughly the same second as the quantity change), and only miscalibrates
   * this one percentage-of-subtotal fee's price, not cart correctness generally.
   */
  #onClick = async (event) => {
    event.preventDefault();
    const button = event.currentTarget;
    const productId = this.dataset.productId;
    button.disabled = true;
    // aria-busy drives .cart-upsell-cta__loader in assets/custom.css - #_ensureFeeLine's network chain takes a
    // couple of seconds, and a disabled button with no other visual change reads as unresponsive rather than
    // "working on it." Also recorded in ctaBusyProducts (not just as this instance's own attribute) so a
    // redraw that replaces this exact element mid-click doesn't drop the spinner - see that field's own
    // comment. Left set on success: this element navigates away immediately after, so there's no later render
    // left to clear it on, and clearing it here would just be visible flicker before the navigation takes over.
    CartUpsellCta.ctaBusyProducts.add(productId);
    this.setAttribute('aria-busy', 'true');

    try {
      // Always calls _ensureFeeLine, even if this.dataset.lineKey already looks set - that dataset attribute
      // is only as fresh as this element's last render, and _ensureFeeLine re-checks the LIVE cart itself, so
      // this guarantees the fee added matches the current target at the moment of the click, not whatever was
      // true when the button last rendered. See this._sectionId's own comment (#connectedCallback) for why
      // that live re-check is reliable even if a redraw replaces this exact element mid-click - see this
      // method's own comment above for the separate, still-open gap that doesn't close.
      await withLock(() => this._ensureFeeLine());
      window.location.href = `${Shopify.routes.root}checkout`;
    } catch (error) {
      CartUpsellCta.ctaBusyProducts.delete(productId);
      button.disabled = false;
      this.removeAttribute('aria-busy');
      this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error?.message || error } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  };
}

if (!window.customElements.get('cart-upsell-toggle')) {
  window.customElements.define('cart-upsell-toggle', CartUpsellToggle);
}

if (!window.customElements.get('cart-upsell-cta')) {
  window.customElements.define('cart-upsell-cta', CartUpsellCta);
}
